import type {SceneId} from '../store/types';

/* ===========================================================================
   Pictures without files.
   ---------------------------------------------------------------------------
   A template cannot ship photographs — licences, weight, and the fact that a
   demo flat is not a real flat. So every seeded picture is RENDERED from one
   small 3D model of a room:

     flatArt()      a perspective view, for cards and galleries
     panoramaArt()  the same room as a 360° equirectangular image

   Both call the same `createSampler`, which answers one question: "looking in
   this direction from the middle of the room, what colour is there?" That is
   the whole renderer, and it is why the photo on a card and the panorama the
   visitor then drags around are the same room rather than two unrelated
   pictures — the thing that makes a listing feel real.

   Everything is deterministic in the seed, so a listing keeps its own rooms
   across reloads, and everything is cached, so a grid re-render is free.
   =========================================================================== */

function hash(value: string): number {
  let out = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    out ^= value.charCodeAt(index);
    out = Math.imul(out, 16777619);
  }
  return out >>> 0;
}

function rng(seed: number): () => number {
  let state = seed || 1;
  return () => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Stable 0..1 noise for a pair of integers — plank tone, tile tone, grain. */
function cell(a: number, b: number, salt: number): number {
  let t = Math.imul(a * 374761393 + b * 668265263 + salt * 2246822519, 3266489917);
  t = (t ^ (t >>> 15)) >>> 0;
  return t / 4294967296;
}

type RGB = [number, number, number];

interface Room {
  /** metres */
  w: number;
  h: number;
  d: number;
  eye: number;
  wall: RGB;
  floor: RGB;
  accent: RGB;
  floorKind: 'wood' | 'tile';
  outdoor?: boolean;
}

const ROOMS: Record<SceneId, Room> = {
  'ban-cong': {accent: [86, 120, 92], d: 3, eye: 1.6, floor: [176, 168, 156], floorKind: 'tile', h: 3, outdoor: true, w: 4, wall: [222, 216, 204]},
  bep: {accent: [126, 106, 78], d: 3.6, eye: 1.6, floor: [196, 188, 176], floorKind: 'tile', h: 2.9, w: 4, wall: [232, 227, 216]},
  'mat-tien': {accent: [110, 122, 96], d: 6, eye: 1.6, floor: [166, 160, 150], floorKind: 'tile', h: 3, outdoor: true, w: 6, wall: [226, 220, 208]},
  'phong-khach': {accent: [80, 112, 92], d: 5.4, eye: 1.6, floor: [186, 146, 104], floorKind: 'wood', h: 3.2, w: 6.2, wall: [234, 229, 219]},
  'phong-ngu': {accent: [120, 104, 132], d: 4.2, eye: 1.5, floor: [178, 140, 100], floorKind: 'wood', h: 3, w: 4.6, wall: [231, 225, 216]},
  'phong-tam': {accent: [96, 118, 124], d: 2.6, eye: 1.6, floor: [206, 202, 196], floorKind: 'tile', h: 2.8, w: 2.8, wall: [224, 224, 220]},
  'san-vuon': {accent: [104, 128, 88], d: 8, eye: 1.6, floor: [128, 146, 108], floorKind: 'tile', h: 3, outdoor: true, w: 8, wall: [220, 216, 206]},
};

type Sampler = (dx: number, dy: number, dz: number, out: RGB) => void;

/**
 * The room, as a function from direction to colour.
 *
 * The model is deliberately an EMPTY room with real light: walls, a floor, a
 * window, a door, a framed picture, a rug. Furniture modelled out of boxes
 * would read as clip art, while an empty flat with light falling across it is
 * exactly what a property listing photographs like.
 */
function createSampler(scene: SceneId, seed: string): Sampler {
  const random = rng(hash(`${scene}|${seed}`));
  const room = ROOMS[scene] ?? ROOMS['phong-khach'];

  const hw = room.w / 2;
  const hd = room.d / 2;
  const bottom = -room.eye;
  const top = room.h - room.eye;

  const winHalf = Math.min(hw - 0.4, 1.1 + random() * 0.5);
  const winY0 = bottom + 0.85;
  const winY1 = Math.min(top - 0.25, winY0 + 1.7);
  const lightY = (winY0 + winY1) / 2;
  const doorX = (random() - 0.5) * (room.w - 1.6);
  const rugRx = Math.min(hw - 0.5, 1.7);
  const rugRz = Math.min(hd - 0.5, 1.2);

  // What is seen out of the window, and all around in the outdoor scenes.
  const skyline: Array<{a: number; half: number; top: number; tone: number}> = [];
  for (let index = 0; index < 14; index += 1) {
    skyline.push({
      a: -Math.PI + (index / 14) * Math.PI * 2 + (random() - 0.5) * 0.2,
      half: 0.12 + random() * 0.2,
      tone: 0.55 + random() * 0.3,
      top: 0.1 + random() * 0.34,
    });
  }

  function sky(elevation: number, azimuth: number, out: RGB) {
    const t = Math.max(0, Math.min(1, (elevation + 0.25) / 1.2));
    let r = 210 - t * 44;
    let g = 222 - t * 32;
    let b = 230 - t * 10;
    const dx = Math.abs(((azimuth - 0.9 + Math.PI) % (Math.PI * 2)) - Math.PI);
    const dy = elevation - 0.5;
    const glow = Math.exp(-(dx * dx + dy * dy) * 6);
    r += glow * 34;
    g += glow * 30;
    b += glow * 20;
    if (elevation < 0.3) {
      for (const building of skyline) {
        const delta = Math.abs(((azimuth - building.a + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
        // A building stands ON the horizon and rises from it. Letting one float
        // between two elevations is what turns a skyline into grey confetti.
        if (delta < building.half && elevation < building.top && elevation > -0.5) {
          // A grid of windows, in the building's own angular coordinates. It is
          // the difference between "a city out there" and four grey slabs.
          const u = delta / building.half;
          const v = Math.max(0, elevation) / building.top;
          const lit =
            (u * 9) % 1 < 0.58 && (v * 11) % 1 < 0.52
              ? 1.16 + cell(Math.floor(u * 9), Math.floor(v * 11), 23) * 0.2
              : 1;
          const shade = building.tone * 0.74 * lit;
          r = 186 * shade;
          g = 191 * shade;
          b = 196 * shade;
          break;
        }
      }
    }
    out[0] = r;
    out[1] = g;
    out[2] = b;
  }

  return (dx, dy, dz, out) => {
    if (room.outdoor) {
      const elevation = dy;

      // A balcony without a railing is a roof edge. One plane of vertical bars
      // 1.2m in front of the viewer is enough to say which one this is, and it
      // gets real perspective for free because it is traced, not painted.
      if (scene === 'ban-cong' && dz > 0.05) {
        const t = 1.2 / dz;
        const hx = dx * t;
        const hy = dy * t;
        if (hy > bottom && hy < bottom + 1.05 && Math.abs(hx) < 2.4) {
          const bar = hx / 0.14 - Math.floor(hx / 0.14);
          if (bar < 0.34 || hy > bottom + 0.96) {
            const k = hy > bottom + 0.96 ? 0.9 : 0.72;
            out[0] = 96 * k;
            out[1] = 94 * k;
            out[2] = 88 * k;
            return;
          }
        }
      }

      if (dy > -0.001) {
        sky(elevation, Math.atan2(dx, dz), out);
        return;
      }
      const t = -bottom / -dy;
      const px = dx * t;
      const pz = dz * t;
      const tile = (Math.floor(px / 0.9) + Math.floor(pz / 0.9)) % 2 === 0 ? 1 : 0.94;
      const fade = 1 / (1 + t * 0.012);
      const noise = cell(Math.floor(px * 2), Math.floor(pz * 2), 3) * (scene === 'san-vuon' ? 0.22 : 0.06);
      const k = tile * (0.86 + noise) * (0.55 + 0.72 * fade);
      out[0] = room.floor[0] * k;
      out[1] = room.floor[1] * k;
      out[2] = room.floor[2] * k;
      return;
    }

    // Which of the six planes does this direction hit first?
    let t = Infinity;
    let face = 0; // 1 floor, 2 ceiling, 3 ±x wall, 4 ±z wall
    if (dy < -1e-6) {
      const ty = bottom / dy;
      if (ty < t) {
        t = ty;
        face = 1;
      }
    } else if (dy > 1e-6) {
      const ty = top / dy;
      if (ty < t) {
        t = ty;
        face = 2;
      }
    }
    if (dx !== 0) {
      const tx = (dx > 0 ? hw : -hw) / dx;
      if (tx > 0 && tx < t) {
        t = tx;
        face = 3;
      }
    }
    if (dz !== 0) {
      const tz = (dz > 0 ? hd : -hd) / dz;
      if (tz > 0 && tz < t) {
        t = tz;
        face = 4;
      }
    }

    const px = dx * t;
    const py = dy * t;
    const pz = dz * t;

    let nx = 0;
    let ny = 0;
    let nz = 0;
    let base0 = room.wall[0];
    let base1 = room.wall[1];
    let base2 = room.wall[2];
    let emissive = 0;

    if (face === 1) {
      ny = 1;
      const rug = (px / rugRx) ** 2 + (pz / rugRz) ** 2;
      if (rug < 1) {
        const edge = rug > 0.86;
        const k = edge ? 0.78 : 1;
        base0 = room.accent[0] * 0.85 * k + 74;
        base1 = room.accent[1] * 0.85 * k + 74;
        base2 = room.accent[2] * 0.85 * k + 70;
      } else if (room.floorKind === 'wood') {
        const plank = Math.floor(px / 0.17);
        const seam = px / 0.17 - plank < 0.05;
        const tone = 0.86 + cell(plank, Math.floor(pz / 1.4), 7) * 0.24;
        const k = seam ? 0.7 : tone;
        base0 = room.floor[0] * k;
        base1 = room.floor[1] * k;
        base2 = room.floor[2] * k;
      } else {
        const gx = px / 0.62 - Math.floor(px / 0.62);
        const gz = pz / 0.62 - Math.floor(pz / 0.62);
        const grout = gx < 0.03 || gz < 0.03;
        const tone = grout ? 0.82 : 0.94 + cell(Math.floor(px / 0.62), Math.floor(pz / 0.62), 5) * 0.1;
        base0 = room.floor[0] * tone;
        base1 = room.floor[1] * tone;
        base2 = room.floor[2] * tone;
      }
    } else if (face === 2) {
      ny = -1;
      base0 = 244;
      base1 = 242;
      base2 = 236;
      if (px * px + pz * pz < 0.16) emissive = 70;
    } else {
      const onZ = face === 4;
      if (onZ) nz = dz > 0 ? -1 : 1;
      else nx = dx > 0 ? -1 : 1;

      const u = onZ ? px : pz;
      const front = onZ && dz > 0;

      if (front && Math.abs(u) < winHalf && py > winY0 && py < winY1) {
        const bar = Math.abs(u) < 0.035 || Math.abs(py - lightY) < 0.03 ? 0.42 : 1;
        // Looking out: the window frames a narrow slice of the view, so the
        // elevation range through it is small — but not so small that the
        // skyline collapses into one flat band.
        sky(0.1 + (py - lightY) * 0.42, Math.atan2(u, hd) * 1.6, out);
        base0 = out[0] * bar + 24 * bar;
        base1 = out[1] * bar + 24 * bar;
        base2 = out[2] * bar + 22 * bar;
        emissive = 34 * bar;
      } else if (front && Math.abs(u) < winHalf + 0.08 && py > winY0 - 0.08 && py < winY1 + 0.08) {
        base0 = 248;
        base1 = 247;
        base2 = 243;
      } else if (onZ && dz < 0 && Math.abs(u - doorX) < 0.45 && py < bottom + 2.1) {
        const frame = Math.abs(u - doorX) > 0.41 || py > bottom + 2.04;
        const k = frame ? 0.92 : 0.74;
        base0 = 184 * k;
        base1 = 166 * k;
        base2 = 140 * k;
        if (Math.abs(u - doorX - 0.3) < 0.035 && Math.abs(py - (bottom + 1.05)) < 0.035) {
          base0 = 206;
          base1 = 200;
          base2 = 188;
        }
      } else if (py < bottom + 0.11) {
        base0 = room.wall[0] * 0.82;
        base1 = room.wall[1] * 0.82;
        base2 = room.wall[2] * 0.82;
      } else if (scene === 'phong-tam' || scene === 'bep') {
        // A tiled band: what tells these two rooms apart from an empty bedroom.
        if (py > bottom + 0.9 && py < bottom + 1.9) {
          const tu = u / 0.3 - Math.floor(u / 0.3);
          const tv = py / 0.3 - Math.floor(py / 0.3);
          const grout = tu < 0.06 || tv < 0.06;
          const k = grout ? 0.86 : 1.02;
          base0 = (room.accent[0] * 0.3 + 186) * k;
          base1 = (room.accent[1] * 0.3 + 192) * k;
          base2 = (room.accent[2] * 0.3 + 190) * k;
        }
      } else if (!onZ && Math.abs(u) < 0.7 && py > bottom + 1.15 && py < bottom + 1.95) {
        // A framed picture, the only decoration on the wall.
        const border = Math.abs(u) > 0.62 || py < bottom + 1.22 || py > bottom + 1.88;
        if (border) {
          base0 = 96;
          base1 = 84;
          base2 = 68;
        } else {
          base0 = room.accent[0] * 0.6 + 96;
          base1 = room.accent[1] * 0.6 + 102;
          base2 = room.accent[2] * 0.6 + 94;
        }
      }
    }

    // One light: the window. The gain is capped just above 1 on purpose — this
    // palette starts near 240, so a bigger gain clips whole walls to flat white
    // and throws away every corner the geometry just produced.
    const lx = -px;
    const ly = lightY - py;
    const lz = hd - 0.05 - pz;
    const dist = Math.sqrt(lx * lx + ly * ly + lz * lz) || 1;
    const diffuse = Math.max(0, (lx * nx + ly * ny + lz * nz) / dist);
    const falloff = 1 / (1 + 0.05 * dist * dist);
    const ambient = 0.5 + 0.14 * ((py - bottom) / room.h);
    // Corners darker than faces: the cheapest ambient occlusion there is, and
    // the thing that makes a box read as a room.
    const corner =
      1 - 0.16 * Math.exp(-Math.abs(py - bottom) * 3) - 0.1 * Math.exp(-Math.abs(top - py) * 4);
    const k = Math.min(1.04, (ambient + 0.85 * diffuse * falloff) * corner);

    out[0] = base0 * k + emissive;
    out[1] = base1 * k + emissive;
    out[2] = base2 * k + emissive;
  };
}

/* ------------------------------------------------------------------ output */

function canvasOf(width: number, height: number): [HTMLCanvasElement, CanvasRenderingContext2D | null] {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return [canvas, canvas.getContext('2d')];
}

const flatCache = new Map<string, string>();
const panoCache = new Map<string, string>();

/** A 4:3 photograph of the room, taken from a corner chosen by the seed. */
export function flatArt(scene: SceneId, seed: string): string {
  const key = `${scene}|${seed}`;
  const cached = flatCache.get(key);
  if (cached) return cached;

  const W = 720;
  const H = 540;
  const [canvas, context] = canvasOf(W, H);
  if (!context) return '';

  const sample = createSampler(scene, seed);
  const image = context.createImageData(W, H);
  const data = image.data;

  // Different photos of one listing must not be the same wall four times, so
  // the seed picks where the photographer stood — but always facing roughly
  // towards the window (yaw π looks at +z), because that is where the light is
  // and an estate agent has never once photographed a room from the window.
  const random = rng(hash(`view|${key}`));
  const yaw = Math.PI + (random() - 0.5) * 1.4;
  const pitch = -0.08 - random() * 0.06;
  const focal = 0.5 / Math.tan(0.52); // ≈ 60° vertical field of view
  const cy = Math.cos(yaw);
  const sy = Math.sin(yaw);
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);

  const rgb: RGB = [0, 0, 0];
  let offset = 0;

  for (let y = 0; y < H; y += 1) {
    const py = (H / 2 - (y + 0.5)) / H;
    for (let x = 0; x < W; x += 1) {
      const px = (x + 0.5 - W / 2) / H;
      // Camera space: looking down -z, then pitched down and turned by yaw.
      let vx = px;
      let vy = py;
      let vz = -focal;
      const length = Math.sqrt(vx * vx + vy * vy + vz * vz);
      vx /= length;
      vy /= length;
      vz /= length;

      const ry = vy * cp - vz * sp;
      const rz = vy * sp + vz * cp;
      const fx = vx * cy + rz * sy;
      const fz = -vx * sy + rz * cy;

      sample(fx, ry, fz, rgb);

      // A soft vignette: the one thing that stops a synthetic render from
      // looking like a flat swatch when it sits in a card.
      const vignette = 1 - 0.12 * (px * px + py * py) * 2.2;
      const grain = (cell(x, y, 17) - 0.5) * 3;
      data[offset] = rgb[0] * vignette + grain;
      data[offset + 1] = rgb[1] * vignette + grain;
      data[offset + 2] = rgb[2] * vignette + grain;
      data[offset + 3] = 255;
      offset += 4;
    }
  }

  context.putImageData(image, 0, 0);
  const url = canvas.toDataURL('image/jpeg', 0.82);
  flatCache.set(key, url);
  return url;
}

/**
 * The same room as an equirectangular panorama.
 *
 * Every pixel is one direction: x is the compass angle, y the elevation. That
 * is the format every 360 viewer on the web expects, so this output and a real
 * 360 photo from a phone are interchangeable — which is the point: the viewer
 * is never written against fake data.
 */
export function panoramaArt(scene: SceneId, seed: string): string {
  const key = `${scene}|${seed}`;
  const cached = panoCache.get(key);
  if (cached) return cached;

  const W = 1536;
  const H = 768;
  const [canvas, context] = canvasOf(W, H);
  if (!context) return flatArt(scene, seed);

  const sample = createSampler(scene, seed);
  const image = context.createImageData(W, H);
  const data = image.data;
  const rgb: RGB = [0, 0, 0];
  let offset = 0;

  for (let y = 0; y < H; y += 1) {
    const phi = (0.5 - (y + 0.5) / H) * Math.PI;
    const cosPhi = Math.cos(phi);
    const sinPhi = Math.sin(phi);

    for (let x = 0; x < W; x += 1) {
      const theta = ((x + 0.5) / W) * Math.PI * 2 - Math.PI;
      sample(cosPhi * Math.sin(theta), sinPhi, cosPhi * Math.cos(theta), rgb);

      const grain = (cell(x, y, 17) - 0.5) * 3;
      data[offset] = rgb[0] + grain;
      data[offset + 1] = rgb[1] + grain;
      data[offset + 2] = rgb[2] + grain;
      data[offset + 3] = 255;
      offset += 4;
    }
  }

  context.putImageData(image, 0, 0);
  const url = canvas.toDataURL('image/jpeg', 0.86);
  panoCache.set(key, url);
  return url;
}
